import { useEffect, useState } from 'preact/hooks';
import { api, onEntry, type TokenSummary } from '../api.js';
import { Avatar, Meter, useTicker } from '../components/bits.js';
import { zec, pct, ago, usd } from '../format.js';
import { useStore } from '../store.js';

const SORTS: [string, string][] = [
  ['launching', 'Launching now'],
  ['active', 'Trading now'],
  ['new', 'New'],
  ['almost', 'About to graduate'],
  ['graduated', 'Graduated'],
  ['mcap', 'Market cap'],
];

/** Each tab is a server sort plus a filter. */
const VIEW: Record<string, { sort: string; keep?: (t: TokenSummary) => boolean; order?: (a: TokenSummary, b: TokenSummary) => number }> = {
  launching: { sort: 'new', keep: (t) => t.launchOpen },
  active: { sort: 'active' },
  new: { sort: 'new' },
  almost: { sort: 'mcap', keep: (t) => !t.graduated && t.progressBps > 0, order: (a, b) => b.progressBps - a.progressBps },
  graduated: { sort: 'mcap', keep: (t) => t.graduated },
  mcap: { sort: 'mcap' },
};

export function Home({ q }: { q: string }) {
  const [sort, setSort] = useState('active');
  const [list, setList] = useState<TokenSummary[] | null>(null);
  const [all, setAll] = useState<TokenSummary[]>([]);
  const s = useStore();
  useTicker(15_000);

  useEffect(() => {
    let alive = true;
    const load = async () => {
      const v = VIEW[sort] ?? VIEW.active;
      const [l, everything] = await Promise.all([api.tokens(v.sort, q), api.tokens('mcap')]);
      if (!alive) return;
      let out = v.keep ? l.filter(v.keep) : l;
      if (v.order) out = [...out].sort(v.order);
      setList(out);
      setAll(everything);
    };
    void load();
    let pending: ReturnType<typeof setTimeout> | undefined;
    const off = onEntry((e) => {
      if (e.kind !== 'user' && (e.data as { type?: string })?.type !== 'launch_settle') return;
      clearTimeout(pending);
      pending = setTimeout(load, 400);
    });
    return () => {
      alive = false;
      off();
      clearTimeout(pending);
    };
  }, [sort, q]);

  // The coin closest to filling its curve gets the spotlight.
  const featured = all.filter((t) => !t.graduated).sort((a, b) => b.progressBps - a.progressBps)[0];

  return (
    <>
      <section class="home-head">
        <div>
          <h1>Launch a coin. Trade it in ZEC. Privately.</h1>
          <p>
            Every coin starts with a fair minute: all buys fill at one price, so snipers and bundlers gain nothing. Nobody can see who trades,
            and every trade is written into Zcash, where anyone can check it.
          </p>
        </div>
        <a class="btn brass" href="#/create" style={{ padding: '14px 22px', fontSize: 17 }}>
          Launch a coin
        </a>
      </section>

      {featured && featured.progressBps > 0 && (
        <a class="featured" href={`#/token/${featured.id}`}>
          <Avatar token={featured} size={96} />
          <div>
            <div class="label">King of the hill · closest to graduation</div>
            <div class="ticker">${featured.symbol}</div>
            <div class="muted">{featured.name}</div>
            <div style={{ marginTop: 10, maxWidth: 420 }}>
              <Meter bps={featured.progressBps} />
              <div class="muted" style={{ fontSize: 13, marginTop: 4 }}>
                {(featured.progressBps / 100).toFixed(1)}% of the curve sold
              </div>
            </div>
          </div>
          <div class="hide-sm" style={{ textAlign: 'right' }}>
            <div class="muted" style={{ fontSize: 13 }}>
              Market cap
            </div>
            <div class="display" style={{ fontSize: 40 }}>
              {zec(featured.marketCapZat, 2)} ZEC
            </div>
            <div class="muted">{usd(featured.marketCapZat, s.zecUsd)}</div>
          </div>
        </a>
      )}

      <div class="tabs" role="tablist" aria-label="Sort coins">
        {SORTS.map(([k, label]) => (
          <button role="tab" aria-selected={sort === k} onClick={() => setSort(k)} key={k}>
            {label}
          </button>
        ))}
      </div>
      {q && (
        <p class="muted">
          Coins matching “{q}”. <a href="#/">Show all</a>
        </p>
      )}

      {list === null ? (
        <div class="empty">Loading coins…</div>
      ) : list.length === 0 ? (
        <div class="empty">
          {q
            ? 'No coin matches that search.'
            : sort === 'graduated'
              ? 'No coin has graduated yet.'
              : sort === 'launching'
                ? 'No coin is in its launch minute right now.'
                : sort === 'almost'
                  ? 'No coin has started filling its curve yet.'
                  : 'No coins yet.'}{' '}
          <a href="#/create">Launch the first one</a>
        </div>
      ) : (
        <div class="rows">
          <div class="row rows-head" aria-hidden="true">
            <span />
            <span>Coin</span>
            <span class="hide-sm">Market cap</span>
            <span class="hide-sm">24h</span>
            <span class="hide-sm">Curve</span>
            <span class="hide-sm">Age</span>
            <span class="show-sm">Market cap</span>
          </div>
          {list.map((t) => (
            <a class="row" href={`#/token/${t.id}`} key={t.id}>
              <Avatar token={t} size={44} />
              <div style={{ minWidth: 0 }}>
                <div class="ticker">
                  ${t.symbol}
                  {t.launchOpen && <span class="badge-live">fair launch</span>}
                </div>
                <div class="name">{t.name}</div>
              </div>
              <div class="hide-sm">
                <div class="num">{zec(t.marketCapZat, 2)} ZEC</div>
                <div class="sub">{usd(t.marketCapZat, s.zecUsd) ?? `${t.trades} trades`}</div>
              </div>
              <div class={`hide-sm num ${t.change24h > 0 ? 'up' : t.change24h < 0 ? 'down' : 'muted'}`}>{pct(t.change24h)}</div>
              <div class="hide-sm">
                <Meter bps={t.progressBps} graduated={t.graduated} />
                <div class="sub" style={{ marginTop: 4 }}>
                  {t.graduated ? 'Graduated' : `${(t.progressBps / 100).toFixed(1)}%`}
                </div>
              </div>
              <div class="muted hide-sm">{ago(t.createdTs)}</div>
              <div class="show-sm">
                <div class="num">{zec(t.marketCapZat, 2)} ZEC</div>
                <div class={`sub ${t.change24h > 0 ? 'up' : t.change24h < 0 ? 'down' : ''}`}>
                  {pct(t.change24h)} · {t.graduated ? 'graduated' : `${(t.progressBps / 100).toFixed(0)}% curve`}
                </div>
              </div>
            </a>
          ))}
        </div>
      )}
    </>
  );
}
